import { useState, useEffect } from 'react'
import Link from '@/components/ui/Link'
import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'

interface SubmenuItem {
	id: string
	label: string
	href: string
}

interface MobileMenuProps {
	isOpen: boolean
	data: any
	onClose: () => void
	onLinkClick: () => void
	menuCategories: Array<{
		label: string
		href: string
		submenu: Array<{
			label: string
			href: string
		}>
	}>
	aboutButtons: SubmenuItem[]
	galleryCategories: SubmenuItem[]
}

export default function MobileMenu({
	isOpen,
	data,
	onClose,
	onLinkClick,
	menuCategories,
	aboutButtons,
	galleryCategories,
}: MobileMenuProps) {
	const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)
	const [isVisible, setIsVisible] = useState(false)
	const [activeSection, setActiveSection] = useState<
		'main' | 'about' | 'gallery'
	>('main')
	const [isLoading, setIsLoading] = useState(false)

	// Update visibility when isOpen changes
	useEffect(() => {
		if (isOpen) {
			setIsVisible(true)
			setIsLoading(true)
			// Simulate loading for better UX
			setTimeout(() => setIsLoading(false), 100)
		} else {
			const timer = setTimeout(() => {
				setIsVisible(false)
			}, 300) // 300ms delay before hiding
			return () => clearTimeout(timer)
		}
	}, [isOpen])

	const getMenuId = (item: { label: string; href: string }) => {
		return `${item.label}-${item.href}`.replace(/\s+/g, '-').toLowerCase()
	}

	const toggleSubmenu = (item: { label: string; href: string }) => {
		const menuId = getMenuId(item)
		setOpenSubmenuId(openSubmenuId === menuId ? null : menuId)
	}

	const handleSectionChange = (section: 'main' | 'about' | 'gallery') => {
		setActiveSection(section)
		setOpenSubmenuId(null)
	}

	// Transform API data to match the expected structure (same as in Header)
	const transformedMenuCategories = data?.navigation?.menu_items?.map((item: any) => ({
		label: item.menu_items_id?.label || '',
		href: item.menu_items_id?.href || '',
		submenu: item.menu_items_id?.submenu || []
	})) || []

	// Transform nav_about data for About Buttons Grid (same as in Header)
	const transformedAboutButtons: SubmenuItem[] = data?.nav_about?.submenu || []

	// Transform nav_gallery data for Gallery Categories (same as in Header)
	const transformedGalleryCategories: SubmenuItem[] = data?.nav_gallery?.submenu || []

	// Use transformed data if available, otherwise fall back to passed props
	const displayMenuCategories = transformedMenuCategories.length > 0 ? transformedMenuCategories : menuCategories
	const displayAboutButtons = transformedAboutButtons.length > 0 ? transformedAboutButtons : aboutButtons
	const displayGalleryCategories = transformedGalleryCategories.length > 0 ? transformedGalleryCategories : galleryCategories

	return (
		<div
			className={`lg:hidden fixed inset-0 top-[70px] bg-white z-[9000] overflow-y-auto transition-all duration-300 ${
				isOpen && isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full pointer-events-none'
			}`}
		>
			{/* Mobile Menu Header */}
			<div className='sticky top-0 bg-white border-b border-gray-200 px-4 py-4 z-[999] shadow-sm animate-in slide-in-from-top-2 duration-300'>
				{/* Section Navigation Tabs */}
				<div className='flex border-b border-gray-200'>
					<button
						onClick={() => handleSectionChange('main')}
						className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
							activeSection === 'main'
								? 'text-black border-b-2 border-black'
								: 'text-gray-500 hover:'
						}`}
					>
						Services
					</button>
					<button
						onClick={() => handleSectionChange('about')}
						className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
							activeSection === 'about'
								? 'text-black border-b-2 border-black'
								: 'text-gray-500 hover:'
						}`}
					>
						About
					</button>
					<button
						onClick={() => handleSectionChange('gallery')}
						className={`flex-1 py-3 text-center text-sm font-medium transition-colors ${
							activeSection === 'gallery'
								? 'text-black border-b-2 border-black'
								: 'text-gray-500 hover:'
						}`}
					>
						Gallery
					</button>
				</div>

				{/* Quick Actions */}
				<div className='flex gap-3 mt-4'>
					<Link
						href='/contact-us/'
						onClick={onLinkClick}
						className='flex-1 px-4 py-3 bg-black text-white text-left text-sm font-bold uppercase tracking-wide rounded-lg hover:bg-gray-800 transition-colors shadow-sm flex items-center justify-center gap-2'
					>
						<svg
							className='w-4 h-4 flex-shrink-0'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
						>
							<rect x='3' y='4' width='18' height='18' rx='2' ry='2' />
							<line x1='16' y1='2' x2='16' y2='6' />
							<line x1='8' y1='2' x2='8' y2='6' />
							<line x1='3' y1='10' x2='21' y2='10' />
						</svg>
						<span className='leading-tight'>Contact Us</span>
					</Link>
					<Link
						href={`tel:${(data?.phone_number?.title || '0208 748 2860').replace(/\s/g, '')}`}
						onClick={onLinkClick}
						className='flex-1 px-4 py-3 bg-white border border-black text-black text-center text-sm font-bold uppercase tracking-wide rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center justify-center gap-2'
					>
						<svg
							className='w-4 h-4 flex-shrink-0'
							viewBox='0 0 24 24'
							fill='currentColor'
						>
							<path d='M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z' />
						</svg>
						Call Now
					</Link>
				</div>
			</div>

			{/* Mobile Menu Content */}
			<div className='px-4 py-6 animate-in fade-in duration-300 delay-100'>
				{isLoading ? (
					<div className='flex items-center justify-center py-12'>
						<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-black'></div>
					</div>
				) : (
					<>
					{/* Main Services Section */}
					<div className={activeSection === 'main' ? 'space-y-2' : 'hidden'}>
						{displayMenuCategories.map(category => {
									const menuId = getMenuId(category)
									const hasSubmenu =
										category.submenu && category.submenu.length > 0

									return (
										<div key={menuId} className='border-b border-gray-100 pb-3'>
											<div className='flex items-center justify-between'>
												<Link
													href={category.href}
													onClick={onLinkClick}
													className='flex-1 py-3 text-base font-medium text-black hover: transition-colors'
												>
													{category.label}
												</Link>
												{hasSubmenu && (
													<button
														onClick={() => toggleSubmenu(category)}
														className='w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors'
														aria-label='Toggle submenu'
													>
														<svg
															width='16'
															height='16'
															viewBox='0 0 24 24'
															fill='none'
															className={`transform transition-transform ${
																openSubmenuId === menuId ? 'rotate-180' : ''
															}`}
														>
															<path
																d='M6 9L12 15L18 9'
																stroke='currentColor'
																strokeWidth='2'
																strokeLinecap='round'
																strokeLinejoin='round'
															/>
														</svg>
													</button>
												)}
											</div>

										{/* Submenu Items - always in DOM for SEO, CSS controls visibility */}
										{hasSubmenu && (
											<div className={`ml-4 space-y-2 border-l-2 border-gray-200 pl-4 overflow-hidden transition-all duration-300 ${
												openSubmenuId === menuId ? 'mt-3 max-h-[500px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none'
											}`}>
												{category.submenu.map(subItem => (
													<Link
														key={getMenuId(subItem)}
														href={subItem.href}
														onClick={onLinkClick}
														className='block py-2 text-sm hover:text-black transition-colors'
													>
														{subItem.label}
													</Link>
												))}
											</div>
										)}
										</div>
									)
								})}
					</div>

					{/* About Section - always in DOM for SEO */}
					<div className={activeSection === 'about' ? 'space-y-6' : 'hidden'}>
						{/* Meet Dr. De Silva */}
						<Link href={data?.nav_meet?.href || ''} onClick={onLinkClick}>
							<div className='flex gap-4 items-center p-4 bg-gray-50 rounded-lg'>
								<div className='w-20 h-20 relative overflow-hidden rounded-lg shadow-sm'>
									<Image
										src='/images/nav-about-img2.webp'
										alt='Dr. Julian De Silva'
										fill
										className='object-cover'
									/>
								</div>
								<div>
									<h3 className='text-lg font-bold text-black uppercase tracking-wide'>
										MEET {data?.nav_meet?.label || 'Meet Dr. De Silva'}
									</h3>
									<p className='text-sm mt-1'>
										Expert facial plastic surgeon
									</p>
								</div>
							</div>
						</Link>

						{/* About Buttons Grid with Submenu Support */}
						<div className='grid grid-cols-2 gap-3'>
							{displayAboutButtons.map(button => (
								<Link
									key={button.href}
									href={button.href}
									onClick={onLinkClick}
									className='px-4 py-3 bg-white border border-gray-300 rounded-lg text-center text-xs font-bold uppercase tracking-wide hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm'
								>
									{button.label}
								</Link>
							))}
						</div>

						{/* Social Media Links */}
						<div className='mt-6'>
							<h4 className='text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 text-center'>
								Follow Us
							</h4>
							<div className='flex justify-center gap-4'>
								{(data?.social_links || []).map(social => (
									<Link
										key={social.external_links_id.url}
										href={social.external_links_id.url}
										target='_blank'
										onClick={onLinkClick}
										className='w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors'
										aria-label={social.external_links_id.label}
									>
										<Image
											src={getReadableImageUrl(
												social.external_links_id.icon.id,
												social.external_links_id.icon.filename_disk
											)}
											alt={social.external_links_id.label || ''}
											width={20}
											height={20}
											className='w-5 h-5'
											quality={95}
											loading='lazy'
										/>
									</Link>
								))}
							</div>
						</div>
					</div>

					{/* Gallery Section - always in DOM for SEO */}
					<div className={activeSection === 'gallery' ? 'space-y-6' : 'hidden'}>
						<h2 className='text-3xl font-light text-black text-center font-freightBig'>
							{data?.nav_gallery?.label || 'Before & After Gallery'}
						</h2>

						{/* Gallery Image */}
						<Link
							href='/gallery/'
							className='block w-full relative rounded-lg overflow-hidden shadow-sm'
						>
							<Image
								src='/images/nav-ba-img.webp'
								alt='Before & After Photo'
								width={400}
								height={300}
								className='object-cover object-top w-full h-full'
							/>
						</Link>

						{/* Gallery Filter Buttons with Submenu Support */}
						<div className='space-y-3'>
							{displayGalleryCategories.map(category => (
								<Link
									key={category.href}
									href={category.href}
									onClick={onLinkClick}
									className='block w-full px-6 py-4 bg-white border border-gray-300 rounded-lg text-center text-sm font-bold uppercase tracking-wide hover:bg-gray-50 hover:border-gray-400 transition-colors shadow-sm'
								>
									{category.label}
								</Link>
							))}
						</div>
					</div>
					</>
				)}
			</div>
		</div>
	)
}
